import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { usePortalAnchorRect } from "../lib/usePortalAnchorRect";

/**
 * SingleSelectDropdown — searchable, keyboard-friendly single-select combobox.
 *
 * Unlike MultiSelectDropdown, this shows radio-style rows (no checkboxes) and
 * calls `onChange(value)` with a single scalar. Great for "pick one client" UX.
 *
 * Props:
 *   label:     placeholder shown in the trigger when nothing is selected
 *   options:   [{ value, label, sub? }]
 *   value:     currently-selected value ("" for none)
 *   onChange:  (nextValue) => void
 *   testId?:   trigger testid
 *   searchable?: boolean (default true when options.length > 5)
 *   emptyHint?: string when no options match search
 *   triggerClassName?: extra classes for the trigger
 *   clearable?: whether the trigger shows an X to clear the selection
 *   disabled?: boolean
 */
export function SingleSelectDropdown({
  label,
  options,
  value,
  onChange,
  testId,
  searchable,
  triggerClassName = "",
  emptyHint = "No matches",
  clearable = true,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const rect = usePortalAnchorRect(open, ref);

  useEffect(() => {
    if (!open) return undefined;
    // Outside-click needs to consider BOTH the anchor AND the portaled
    // panel, since the panel no longer lives inside the anchor's DOM
    // subtree (portal to document.body escapes the parent).
    const onDoc = (e) => {
      const insideAnchor = ref.current && ref.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideAnchor && !insidePanel) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 30);
    if (!open) setQuery("");
  }, [open]);

  const isSearchable = searchable ?? (options?.length ?? 0) > 5;
  const selected = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q) || (o.sub || "").toLowerCase().includes(q));
  }, [options, query]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={testId}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`inline-flex items-center gap-2 justify-between border border-[#27272A] hover:border-zinc-500 bg-[#0A0A0A] px-3 py-2 text-sm text-left ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${triggerClassName}`}
      >
        <span className={`truncate ${selected ? "text-zinc-100" : "text-zinc-500"}`}>
          {selected ? selected.label : label}
        </span>
        <span className="inline-flex items-center gap-1">
          {clearable && selected && !disabled && (
            <X
              className="size-3 text-zinc-500 hover:text-[#FF3B30]"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              data-testid={testId ? `${testId}-clear` : undefined}
            />
          )}
          <ChevronDown className="size-3.5 text-zinc-500" />
        </span>
      </button>
      {open && rect && createPortal(
        <div
          ref={panelRef}
          data-testid={testId ? `${testId}-popover` : undefined}
          className="fixed z-[90] max-w-[360px] max-h-[320px] flex flex-col border border-[#27272A] bg-[#121212] shadow-xl"
          style={{
            // Position below the trigger by default; flip above if the
            // popover would overflow the viewport bottom.
            top: rect.bottom + 4 + 320 > window.innerHeight
              ? Math.max(8, rect.top - 320 - 4)
              : rect.bottom + 4,
            left: Math.max(8, Math.min(rect.left, window.innerWidth - 360 - 8)),
            minWidth: rect.width,
          }}
        >
          {isSearchable && (
            <div className="p-2 border-b border-[#27272A]">
              <div className="relative">
                <Search className="size-3 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  ref={inputRef}
                  data-testid={testId ? `${testId}-search` : undefined}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] pl-7 pr-2 py-1.5 text-xs placeholder:text-zinc-600"
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="p-3 text-xs text-zinc-500 italic">{emptyHint}</div>
            )}
            {filtered.map((o) => {
              const active = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-[#1c1c1e] border-l-2 ${active ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-transparent"}`}
                >
                  <span className="flex-1 min-w-0">
                    <div className="text-zinc-100 truncate">{o.label}</div>
                    {o.sub && <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{o.sub}</div>}
                  </span>
                  {active && <Check className="size-3.5 text-[#FF3B30]" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default SingleSelectDropdown;
